import { captureSentryError } from './sentryUser';
import { isExpectedOpsError } from './expectedErrors';

export { isExpectedOpsError, isTransientOpsNetworkError } from './expectedErrors';

export type OpsWritePath = 'web-ram' | 'sqlite' | 'storage' | 'rpc' | 'select';

export type CaptureOpsFailureParams = {
  module: string;
  op: string;
  error: unknown;
  /** Caller already classified this as expected (permiso, already-open reconciliado, etc.). */
  expected?: boolean;
  itemId?: string;
  ingestError?: string | null;
  applied?: boolean;
  enqueued?: boolean;
  path?: OpsWritePath;
  /** IDs only — no names, notes, or amounts. */
  extra?: Record<string, string | number | boolean | null | undefined>;
  /** Default true for money/stock/cash writes. */
  critical?: boolean;
};

const reportedOpsFailures = new Set<string>();

/** Reset dedupe state (tests). */
export function resetOpsFailureDedupeForTests(): void {
  reportedOpsFailures.clear();
}

export function classifyOpsErrorClass(error: unknown, ingestError?: string | null): string {
  const ingest = ingestError?.trim();
  if (ingest) {
    return slugOpsErrorClass(ingest);
  }
  if (error instanceof Error && error.name && error.name !== 'Error') {
    return slugOpsErrorClass(error.name);
  }
  if (error instanceof Error && error.message.trim()) {
    return slugOpsErrorClass(error.message);
  }
  return 'unknown';
}

function slugOpsErrorClass(raw: string): string {
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || 'unknown';
}

function opsFailureDedupeKey(params: CaptureOpsFailureParams, errorClass: string): string {
  return `${params.op}:${params.itemId ?? 'none'}:${errorClass}`;
}

export function captureOpsFailure(params: CaptureOpsFailureParams): void {
  if (params.expected) return;
  if (isExpectedOpsError(params.error, params.ingestError)) return;

  const errorClass = classifyOpsErrorClass(params.error, params.ingestError);
  const dedupeKey = opsFailureDedupeKey(params, errorClass);
  if (reportedOpsFailures.has(dedupeKey)) return;
  reportedOpsFailures.add(dedupeKey);

  const err =
    params.error instanceof Error
      ? params.error
      : new Error(typeof params.error === 'string' ? params.error : `Ops failure: ${params.op}`);

  const extra: Record<string, unknown> = {
    ingestError: params.ingestError ?? null,
    applied: params.applied ?? null,
    enqueued: params.enqueued ?? null,
    path: params.path ?? null,
    itemId: params.itemId ?? null,
  };
  if (params.extra) {
    for (const [key, value] of Object.entries(params.extra)) {
      if (value !== undefined) extra[key] = value;
    }
  }

  const critical = params.critical !== false;
  captureSentryError(err, {
    fingerprint: ['ops', params.module, params.op, errorClass],
    tags: {
      module: params.module,
      op: params.op,
      // Misma alerta Fase 0 (`user_visible`) + filtro ops; el usuario ya vio el toast de write fallido.
      ...(critical
        ? { 'ops.critical': 'true', user_visible: 'true' }
        : {}),
    },
    ...extra,
  });
}
